
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import NewCostStandardsFilters from '@/modules/TMSModule/components/NewCostStandardsFilters/index.vue'
import NewCostStandardExcel from '@/modules/TMSModule/components/NewCostStandardExcel/index.vue'
import NewCostStandardsActions from '@/modules/TMSModule/components/NewCostStandardsActions/index.vue'

// @types
import {
    GetCostStandardsParams,
    CostStandards,
    CostStandardsCapacity,
} from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'

// @composable
import useActions from '@/common/composable/useActions'

const TABLE_COLUMNS = [
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'invoiceType',
        name: 'Тип перевозки',
    },
    {
        key: 'cost_per_weight',
        name: 'Тг/кг',
    },
    {
        key: 'capacities',
        name: '20 - тонник',
        value: 6,
    },
    {
        key: 'capacities',
        name: '10 - тонник',
        value: 5,
    },
    {
        key: 'capacities',
        name: '5 - тонник',
        value: 3,
    },
    {
        key: 'capacities',
        name: '3 - тонник',
        value: 2,
    },
    {
        key: 'capacities',
        name: 'Газель',
        value: 1,
    },
    {
        key: 'actions',
        name: '',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        NewCostStandardsFilters,
        NewCostStandardsActions,
        NewLimit,
        NewPagination,
        NewTable,
        Loader,
        NewCostStandardExcel,
    },
    setup() {
        const { actions_state, startCreate, startUpdate, closeActions } =
            useActions<CostStandards>()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_excel: false,
            list: [] as CostStandards[],
            filters: {
                limit: 50,
                current_page: 1,
                total_pages: 1,
                total_items: 1,
                //
                fromCityId: null as number | null,
                toCityId: null as number | null,
                transitCityIds: [] as number[],
                invoiceType: null as number | null,
                //
                monthId: 1,
            },
        })

        const loadCostStandards = async () => {
            try {
                state.is_loading = true

                const params: GetCostStandardsParams = {
                    page: state.filters.current_page,
                    limit: state.filters.limit,
                    //
                    fromCityId: state.filters.fromCityId,
                    toCityId: state.filters.toCityId,
                    transitCityIds: state.filters.transitCityIds,
                    invoiceType: state.filters.invoiceType,
                    //
                    monthId: state.filters.monthId,
                }

                const response = await tmsAPI.getCostStandards(params)
                state.list = response.data.data

                if (response.data.meta) {
                    state.filters.total_items = response.data.meta.total
                    state.filters.total_pages = response.data.meta.last_page
                }

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersChanged = (filters: GetCostStandardsParams) => {
            state.filters.fromCityId = filters.fromCityId as number | null
            state.filters.toCityId = filters.toCityId as number | null
            state.filters.transitCityIds = filters.transitCityIds as number[]
            state.filters.invoiceType = filters.invoiceType as number | null

            state.filters.monthId = filters.monthId as number
            state.filters.current_page = 1

            loadCostStandards()
        }

        const onFiltersReset = () => {
            state.filters.fromCityId = null
            state.filters.toCityId = null
            state.filters.transitCityIds = []
            state.filters.invoiceType = null

            state.filters.monthId = 1
            state.filters.current_page = 1

            loadCostStandards()
        }

        const onLimitChange = (new_limit: number) => {
            state.filters.limit = new_limit
            loadCostStandards()
        }

        const onChangePage = (new_page: number) => {
            state.filters.current_page = new_page
            loadCostStandards()
        }

        onMounted(loadCostStandards)

        const formatValue = (value: string | number | null | undefined, type: string) => {
            if (value || value === 0) {
                if (type === 'p') return `${value.toLocaleString('ru-RU')} тг`
                return `${value.toLocaleString('ru-RU')} тг/м3`
            }

            return '-'
        }

        const onLoadStandardsCreated = () => {
            closeActions()
            loadCostStandards()
        }

        const openExcel = () => {
            state.is_excel = true
        }

        const closeExcel = () => {
            state.is_excel = false
        }

        const onConfirmExcel = () => {
            closeExcel()
            loadCostStandards()
        }

        const getCapacity = (
            id: number,
            capacities: { id: number; costPerCubature: number; cost: number }[]
        ) => {
            return capacities.find((capacity) => capacity.id === id)
        }

        return {
            actions_state,
            startCreate,
            startUpdate,
            closeActions,
            TABLE_COLUMNS,
            state,
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            loadCostStandards,
            formatValue,
            onLoadStandardsCreated,
            openExcel,
            closeExcel,
            onConfirmExcel,
            getCapacity,
        }
    },
})
